import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { constants } from "../../constants"; // Assuming you have a constants file
import EventImage from "../../assets/event-image1.jpeg"; // Fallback image
import { Smile } from "lucide-react";

export default function EventsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${constants.serverURL}/api/events/listEvents`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Format events from the API response
      const formattedEvents = data.events.map((event, index) => ({
        id: event._id || index + 1,
        title: event.title,
        image: event.imgUrl || EventImage, // Use API image or fallback
        name: event.presentedBy,
        date: formatDateRange(event.locations), // Format date range from all locations
        location: formatLocations(event.locations), // Format all locations as a string
        description: event.description,
        eventDetails: event, // Store the full event details for later use
      }));

      setEvents(formattedEvents);
    } catch (error) {
      console.error("Error fetching events:", error);
      setError("Failed to load events. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Helper function to format dates from all locations into a range
  const formatDateRange = (locations) => {
    if (!locations || locations.length === 0) return "Date TBA";

    // Sort locations by date
    const sortedLocations = [...locations].sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );

    const firstDate = new Date(sortedLocations[0].date);
    const lastDate = new Date(sortedLocations[sortedLocations.length - 1].date);

    // Format dates
    const options = { month: "short", day: "numeric", year: "numeric" };

    if (firstDate.getTime() === lastDate.getTime()) {
      return firstDate.toLocaleDateString("en-US", options);
    }

    return `${firstDate.toLocaleDateString(
      "en-US",
      options
    )} - ${lastDate.toLocaleDateString("en-US", options)}`;
  };

  // Helper function to format locations as a string
  const formatLocations = (locations) => {
    if (!locations || locations.length === 0) return "Location TBA";

    if (locations.length === 1) {
      return locations[0].city;
    }

    const cityNames = locations.map((loc) => loc.city);
    return cityNames.join(", ");
  };

  const filteredEvents = events.filter(
    (event) =>
      event.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  Study Abroad Events
                </h2>
                <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                  Check out all the events in your preferred location. Meet
                  university representatives and discuss your study prospects,
                  campus life and more in person.
                </p>

                {/* Tags Grid */}
                <div className="grid grid-cols-2 gap-3">
                  <span className="inline-block bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium">
                    Admission opportunities
                  </span>
                  <span className="inline-block bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium">
                    Co-op / Internship
                  </span>
                  <span className="inline-block bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium">
                    Eligibility criteria
                  </span>
                  <span className="inline-block bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium">
                    Scholarship options
                  </span>
                  <span className="inline-block bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium">
                    Application fee waivers
                  </span>
                  <span className="inline-block bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium">
                    Career outcomes
                  </span>
                </div>
              </div>

              {/* Right Content - Loading Skeleton */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden ml-36">
                <div className="p-6 pb-4">
                  <div className="flex justify-end mb-4">
                    <div className="w-16 h-16 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded animate-pulse mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
                </div>
                <div className="px-6 pb-6">
                  <div className="h-4 bg-gray-200 rounded animate-pulse mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded animate-pulse mb-4 w-1/2"></div>
                  <div className="h-12 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative bg-primary-cream py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-extrabold text-black mb-6">
                Upcoming Immigration Events
              </h1>
              <p className="text-xl text-black max-w-3xl mx-auto">
                Join our informative sessions to learn about immigration
                pathways, study options, and career opportunities abroad.
              </p>
            </div>
          </div>
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10">
            <div className="absolute top-0 right-0 transform translate-x-1/2 -translate-y-1/4 w-96 h-96 bg-white rounded-full"></div>
            <div className="absolute bottom-0 left-0 transform -translate-x-1/3 translate-y-1/4 w-80 h-80 bg-white rounded-full"></div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="bg-white rounded-xl shadow-md overflow-hidden"
              >
                <div className="h-48 bg-gray-200 animate-pulse"></div>
                <div className="p-6">
                  <div className="h-6 bg-gray-200 rounded animate-pulse mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded animate-pulse mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded animate-pulse mb-4 w-3/4"></div>
                  <div className="h-10 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="relative bg-primary-cream py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-extrabold text-black mb-6">
                Upcoming Immigration Events
              </h1>
              <p className="text-xl text-black max-w-3xl mx-auto">
                Join our informative sessions to learn about immigration
                pathways, study options, and career opportunities abroad.
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="text-center py-12 justify-center">
            <div className=" mb-4 mx-auto flex justify-center">
              <Smile size={80} />
            </div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              No events are available at the moment.
            </h3>
            <p className="text-gray-600">Please check back later.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Study Abroad Events
              </h2>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                Check out all the events in your preferred location. Meet
                university representatives and discuss your study prospects,
                campus life and more in person.
              </p>

              {/* Tags Grid */}
              <div className="grid grid-cols-2 gap-3">
                <span className="inline-block bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium">
                  Admission opportunities
                </span>
                <span className="inline-block bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium">
                  Co-op / Internship
                </span>
                <span className="inline-block bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium">
                  Eligibility criteria
                </span>
                <span className="inline-block bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium">
                  Scholarship options
                </span>
                <span className="inline-block bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium">
                  Application fee waivers
                </span>
                <span className="inline-block bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium">
                  Career outcomes
                </span>
              </div>
            </div>

            {/* Right Content - First Event Card */}
            {filteredEvents.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden ml-36">
                <div className="p-6 pb-4">
                  {/* Fixed aspect ratio container for image */}
                  <div className="relative w-full h-auto mb-4 bg-white p-[12px] rounded-2xl shadow-sm border border-gray-100 flex items-center justify-center overflow-hidden">
                    <img
                      src={constants.serverURL + filteredEvents[0].image}
                      alt={filteredEvents[0].title || "Event image"}
                      className="w-full h-auto object-contain object-center rounded-lg"
                      onError={(e) => {
                        // Fallback in case image fails to load
                        e.target.style.display = "none";
                        e.target.parentElement.innerHTML = `
              <div class="w-full h-full bg-gray-200 flex items-center justify-center">
                <svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                </svg>
              </div>
            `;
                      }}
                    />
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">
                    {filteredEvents[0].title}
                  </h3>
                </div>

                <div className="px-6 pb-6">
                  <div className="text-gray-600 mb-4">
                    <p className="text-sm">{filteredEvents[0].date}</p>
                    <p className="text-sm flex items-center mt-1">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2 flex-shrink-0"></span>
                      In-Person Event
                    </p>
                  </div>

                  <button
                    onClick={() =>
                      navigate(`/event-details/${filteredEvents[0].id}`)
                    }
                    className="w-full bg-red-500 text-white py-3 px-6 rounded-lg font-semibold hover:bg-red-600 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                  >
                    Register Now
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="relative bg-primary-cream py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-extrabold text-black mb-6">
              Upcoming Immigration Events
            </h1>
            <p className="text-xl text-black max-w-3xl mx-auto">
              Join our informative sessions to learn about immigration pathways,
              study options, and career opportunities abroad.
            </p>
          </div>
        </div>

        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10">
          <div className="absolute top-0 right-0 transform translate-x-1/2 -translate-y-1/4 w-96 h-96 bg-white rounded-full"></div>
          <div className="absolute bottom-0 left-0 transform -translate-x-1/3 translate-y-1/4 w-80 h-80 bg-white rounded-full"></div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {filteredEvents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredEvents.map((event) => (
              <EventCard key={event.id} event={event} navigate={navigate} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <h3 className="text-xl font-medium text-gray-900">
              No events found matching your search.
            </h3>
            <p className="mt-2 text-gray-600">
              Try adjusting your search terms or browse all our events.
            </p>
            <button
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
              onClick={() => setSearchQuery("")}
            >
              View All Events
            </button>
          </div>
        )}
      </div>

      <div className="bg-blue-50 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            About Our Events
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  ></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">
                Expert-Led Sessions
              </h3>
              <p className="text-gray-600">
                All our events are conducted by education specialists with years
                of experience in the field.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  ></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">Regular Updates</h3>
              <p className="text-gray-600">
                Stay informed about the latest education and immigration
                opportunities through our frequent events.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  ></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-2">
                Personalized Guidance
              </h3>
              <p className="text-gray-600">
                Get your specific questions answered during dedicated counseling
                sessions at each event.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EventCard({ event, navigate }) {
  return (
    <div
      className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1 cursor-pointer"
      onClick={() => navigate(`/event-details/${event.id}`)}
    >
      <div className="relative h-auto bg-white p-[12px] rounded-2xl shadow-sm border border-gray-100 flex items-center justify-center overflow-hidden">
        {/* <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent z-10"></div> */}
        <img
          src={constants.serverURL + event.image || EventImage}
          alt={event.title}
          className="w-full h-auto object-contain rounded-lg"
        />
        <div className="absolute bottom-2 left-2 right-2 p-2 z-20">
          <span className="inline-block bg-blue-600/90 px-3 py-1 text-xs font-semibold rounded-full text-white backdrop-blur-sm">
            {event.date}
          </span>
        </div>
      </div>

      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">
          {event.title}
        </h3>

        <div className="flex items-center mb-4">
          <div className="h-8 w-8 flex-shrink-0 rounded-full bg-gray-300 flex items-center justify-center mr-2">
            <svg
              className="h-4 w-4 text-gray-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <span className="text-gray-700 line-clamp-1">{event.name}</span>
        </div>

        <div className="flex items-center text-gray-600 mb-3">
          <svg
            className="h-5 w-5 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          <span className="text-sm">{event.location}</span>
        </div>

        <p
          className="text-gray-600 mb-4 line-clamp-2"
          dangerouslySetInnerHTML={{ __html: event.description }}
        ></p>

        <button className="w-full py-2 px-4 border border-blue-600 rounded-md font-medium text-blue-600 hover:bg-blue-50 transition focus:outline-none">
          View Details
        </button>
      </div>
    </div>
  );
}
